import { Subject } from 'rxjs';

import { CamRecord } from './record';

export type CamStoreChangeAction = 'add' | 'remove' | 'update' | 'dataset';

export type CamStoreChange<TRecord> = {
  action: CamStoreChangeAction;
  records: TRecord[];
};

export type CamStoreBeforeAddEvent<TRecord extends CamRecord = CamRecord> = {
  parent: TRecord | null;
  records: TRecord[];
  source: CamStore<TRecord>;
};

export class CamStore<TRecord extends CamRecord = CamRecord> {
  public onBeforeAdd: ((event: CamStoreBeforeAddEvent<TRecord>) => boolean | void) | null = null;

  public changes$ = new Subject<CamStoreChange<TRecord>>();

  public records: TRecord[] = [];

  public setRecords(records: TRecord[]): void {
    this.records = [...records];
    this.changes$.next({ action: 'dataset', records: this.records });
  }

  public add(record: TRecord | TRecord[]): TRecord[] {
    const records = Array.isArray(record) ? record : [record];
    if (this.onBeforeAdd && this.onBeforeAdd({ parent: null, records, source: this }) === false) {
      return [];
    }
    this.records = [...this.records, ...records];
    this.changes$.next({ action: 'add', records });
    return records;
  }

  public remove(record: TRecord | TRecord[]): void {
    const records = Array.isArray(record) ? record : [record];
    this.records = this.records.filter(existing => !records.includes(existing));
    this.changes$.next({ action: 'remove', records });
  }

  public getById(id: string | number): TRecord | undefined {
    return this.records.find(record => record.id === id);
  }

  public update(records: TRecord[] = this.records): void {
    this.changes$.next({ action: 'update', records });
  }

  public destroy(): void {
    this.records = [];
    this.onBeforeAdd = null;
    this.changes$.complete();
  }
}
